import { proStudioUrqlGql as urqlGql } from '@pypestream/api-services';
import { Pype } from '../xstate/prostudio.api-service.types';
import { checkForErrors } from './check-for-errors';
import { getCustomerPypes } from './get-customer-pypes';
import { mergePypesById } from './merge-pypes-by-id';

/**
 * `autoresponders` lives on the `Pype` GraphQL type, not on `V6CustomersInfo`
 * (what GetCustomerPypesV6 returns), so it can't be added to that query.
 * Fetched here via the separate Pype-returning bulk endpoint and merged in by
 * id — same "one bulk request, not one per pype" reasoning as
 * load-error-handling.ts.
 */
export const getDefaultMessagesPypes = async ({
  uniqueCustomerId,
  projectId,
  botId,
}: {
  uniqueCustomerId: string;
  projectId: string;
  /**
   * Scopes the result to the selected solution. Default Messages lives inside a
   * solution, so without this the page listed every pype on the customer —
   * including other solutions' channels and the Live Agent Platform connectors.
   * A legacy solution passes every id its streams may carry. The autoresponders request below is customer-wide (that endpoint takes no
   * bot filter), but its values are merged by id, so filtering the pype list is
   * what decides the rows.
   */
  botId?: string | string[];
}): Promise<{ live: Pype[]; sandbox: Pype[] }> => {
  // Several ids for a legacy solution (see `resolveBotSolutionIds`): one read each.
  const botIds = Array.isArray(botId) ? botId : [botId];
  const [reads, autorespondersResponse] = await Promise.all([
    Promise.all(
      botIds.map((id) =>
        getCustomerPypes({ uniqueCustomerId, projectId, botId: id })
      )
    ),
    urqlGql.getCustomersPypesAutoresponders({
      customerId: uniqueCustomerId,
      projectId,
    }),
  ]);

  checkForErrors(autorespondersResponse);

  const liveAgentPypes = {
    live: mergePypesById(reads.map((read) => read.live)),
    sandbox: mergePypesById(reads.map((read) => read.sandbox)),
  };

  const autorespondersById = new Map(
    [
      ...(autorespondersResponse.data?.pypesGetCustomersPypes ?? []),
      ...(autorespondersResponse.data?.pypesGetCustomersPypesSandbox ?? []),
    ].map((pype) => [pype.id, pype.autoresponders])
  );

  const withAutoresponders = (pypes: Pype[]): Pype[] =>
    pypes.map((pype) => ({
      ...pype,
      autoresponders: (pype.id && autorespondersById.get(pype.id)) || null,
    }));

  return {
    live: withAutoresponders(liveAgentPypes.live),
    sandbox: withAutoresponders(liveAgentPypes.sandbox),
  };
};
